// Module 12 — app / composition root (production boot). Implements ROADMAP §12, R1 (zero-server,
// client-only). The ONLY module that composes across the frozen modules; it binds the REAL worker +
// Cesium + dashboards and hands them to the injectable composeApp() (unit-tested in compose.test.ts).
// This glue file is exercised only at runtime — a type:'module' Worker + CesiumJS can't run under
// vitest — so it just type-checks. Boundary (CLAUDE.md #2): import module public entries + `domain`
// + npm only. R1: every fetch is a same-origin GET of bundled offline assets — no network egress.
import { sim, setTransport } from "../state/index";
import { createSimWorkerClient, buildSimModel } from "../worker/index";
import type { WorkerLike, WorkerToMain, MainToWorker, PackContext } from "../worker/index";
import { createCesiumAdapter } from "../viz/index";
import type { CesiumAdapter, VizModel, BasemapKind, DisplayExtras, VizDebug, QualityTier } from "../viz/index";
import { mountDashboardsWithModel } from "../dashboards/index";
import type { SimModel } from "../engine/index";
import type { Scenario } from "../domain/index";
import { makeGovernor } from "./governor";
import { mountPerfHud } from "./perf-hud";
import { mountElementClearance } from "./element-clearance";
import { mountDraggablePanels, type DragHandle } from "./draggable-panels";
import { composeApp } from "./compose";
import { readEnvSignals, recommendedStartTier, watchBattery, type EnvSignals } from "./env-signals";
import { isMobilePhone } from "./mobile";

// data lives under the deploy base (GitHub Pages project path → import.meta.env.BASE_URL ends in "/").
const MODEL_URL = `${import.meta.env.BASE_URL}data`;

// Local-viewing default: boot onto an online basemap (OSM) so the operator sees a real map. R1's
// offline cold-boot stays the viz module default; the app opts into online here (viz's documented
// user-opt-in network path). Switching it at mount also removes the as-yet-unshipped offline tile
// layer before Cesium's first frame, so a missing /basemap/*.png never halts the render loop.
const INITIAL_BASEMAP: BasemapKind = "osm";

function requireEl(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`app: missing #${id} mount point`);
  return el;
}

// PWA staleness killer. With `registerType:'autoUpdate'` the NEW service worker takes control of an
// already-loaded page (skipWaiting+clientsClaim) — but the page keeps RUNNING the old bundle until a
// reload, so after every rebuild users sat on stale code ("refresh twice" — the recurring "it's
// still broken" trap). When a new SW takes over an existing controlled page, reload ONCE so the user
// always lands on the freshest build automatically. The `hadController` guard keeps the very first
// visit (no prior controller → claim fires controllerchange) from reloading needlessly.
function autoReloadOnSwUpdate(): void {
  const sw = navigator.serviceWorker;
  if (!sw) return; // non-secure context / very old browser — nothing to do
  const hadController = sw.controller !== null;
  let reloaded = false;
  sw.addEventListener("controllerchange", () => {
    if (!hadController || reloaded) return;
    reloaded = true;
    window.location.reload();
  });
  // Idle tabs NEVER check for SW updates on their own (checks fire on navigation only) — a tab
  // left open for hours kept running a stale bundle through every rebuild. Poll once a minute;
  // when a new build lands, the SW's force-reload + the listener above bring this page forward.
  setInterval(() => {
    sw.getRegistration().then((r) => r?.update()).catch(() => undefined);
  }, 60_000);
}

// Pick a STARTING quality tier from MULTI-LAYERED device + ambient signals (the adaptive governor then
// floats up/down from here on live frame timing). Layers, each able only to LOWER the hardware baseline:
// software-WebGL → Save-Data/slow network (HARD low) → form factor → low battery off-mains → CPU/memory.
// All browser APIs degrade to neutral defaults where absent (Safari/Firefox), so this never throws and
// never over-promises — the governor still probes upward on real frame timing regardless.
function detectStartTier(env: EnvSignals): QualityTier {
  return recommendedStartTier(env);
}

// Probe a temp WebGL context for the UNMASKED_RENDERER string so env-signals can flag software renderers
// (SwiftShader/llvmpipe). Wrapped so a missing extension or denied context returns null (→ "software").
function probeWebglRenderer(): string | null {
  try {
    const gl = document.createElement("canvas").getContext("webgl");
    if (!gl) return null; // no WebGL at all
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    return ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : "";
  } catch {
    return null;
  }
}

// Build the EnvDeps from the real browser globals. Every field degrades to a safe default where the API
// is absent; the resulting EnvSignals is passed to recommendedStartTier AND surfaced in the perf HUD.
function readBrowserEnv(): EnvSignals {
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    hardwareConcurrency?: number;
    maxTouchPoints?: number;
    connection?: { saveData?: boolean; effectiveType?: string };
    getBattery?: () => Promise<unknown>;
    userAgentData?: { mobile?: boolean };
  };
  return readEnvSignals({
    hardwareConcurrency: nav.hardwareConcurrency,
    deviceMemory: nav.deviceMemory,
    maxTouchPoints: nav.maxTouchPoints,
    connection: nav.connection,
    matchMedia: typeof matchMedia === "function" ? (q) => matchMedia(q) : undefined,
    screenWidth: typeof screen !== "undefined" ? screen.width : undefined,
    screenHeight: typeof screen !== "undefined" ? screen.height : undefined,
    uaMobile: nav.userAgentData?.mobile, // Chromium User-Agent Client Hints; undefined elsewhere
    webglRenderer: probeWebglRenderer,
  });
}

// The "3D" basemap (Google Photorealistic 3D Tiles) needs a DIRECT Google Maps Platform key (NO Cesium
// ion — R1). Resolution order: build-time env (VITE_GOOGLE_MAPS_KEY) → this browser's localStorage →
// the project default below. The project owner supplied this key and asked for it to be baked in so 3D
// works with no prompt. SECURITY: it ships client-side — keep it RESTRICTED in Google Cloud Console
// (HTTP-referrer allow-list + Map Tiles API only) and rotate it if it leaks.
const GMAPS_KEY_LS = "gta:gmapsKey";
const GMAPS_KEY_DEFAULT = "AIzaSyAthpuA0KPXvJLUv-pyaMxtDnOYYEMXunI"; // owner-supplied; restrict + rotate
function resolveGoogleKey(): string | null {
  const env = (import.meta.env as unknown as Record<string, string | undefined>)["VITE_GOOGLE_MAPS_KEY"];
  if (env) return env;
  try { const ls = localStorage.getItem(GMAPS_KEY_LS); if (ls) return ls; } catch { /* private mode */ }
  return GMAPS_KEY_DEFAULT || null;
}
// 3D just works now (the default key resolves) — no prompt. Kept as a thin wrapper so a future
// env/localStorage override still takes precedence; only prompts if every source is somehow empty.
function ensureGoogleKey(): string | null {
  const key = resolveGoogleKey();
  if (key) return key;
  const entered = window.prompt(
    "Enable Google Photorealistic 3D Tiles.\n\nPaste your Google Maps Platform API key (Map Tiles API enabled, " +
    "HTTP-referrer restricted). It is stored only in this browser. Leave blank to cancel.",
  );
  const trimmed = entered ? entered.trim() : "";
  if (!trimmed) return null;
  try { localStorage.setItem(GMAPS_KEY_LS, trimmed); } catch { /* private mode — session only */ }
  return trimmed;
}

// Bottom-of-screen basemap switcher (index.html #basemap-switcher): each button carries a
// data-basemap kind; clicking it routes to viz.setBasemap (viz's user-opt-in online path) and
// moves the .active highlight. The "3D" kind also resolves/prompts for the user's Google key first.
// Optional — absent markup is a no-op so headless boots don't break.
function wireBasemapSwitcher(viz: CesiumAdapter): void {
  const bar = document.getElementById("basemap-switcher");
  if (!bar) return;
  const buttons = Array.from(bar.querySelectorAll<HTMLButtonElement>("button[data-basemap]"));
  const bmTip: Record<string, string> = {
    map: "Dark vector basemap — the default, offline, low-clutter background.",
    satellite: "Satellite imagery basemap (offline raster tiles).",
    photoreal3d: "Google Photorealistic 3D Tiles — real 3D buildings/terrain (needs your own Google Maps key; online).",
  };
  for (const btn of buttons) {
    const t = bmTip[btn.dataset["basemap"] ?? ""] ?? "Switch the map background."; // Wave 2: hover/touch explanation
    btn.title = t; btn.setAttribute("aria-label", t);
    btn.classList.toggle("active", btn.dataset["basemap"] === INITIAL_BASEMAP);
    btn.addEventListener("click", () => {
      const kind = btn.dataset["basemap"] as BasemapKind;
      if (kind === "photoreal3d") {
        const key = ensureGoogleKey();
        if (!key) return; // cancelled — leave the current basemap + highlight untouched
        viz.setBasemap("photoreal3d", { apiKey: key });
      } else {
        viz.setBasemap(kind);
      }
      for (const b of buttons) b.classList.toggle("active", b === btn);
    });
  }
}

// Sun/date clock (index.html #sun-clock): shows the calendar instant the Cesium sun is using — which
// FOLLOWS the simulator's season + hour (viz drives clock.currentTime each render). ‹ › step the
// display year; ☀ toggles globe sun-lighting. Reads the non-contract VizDebug seam. Absent markup = no-op.
function wireSunClock(viz: VizDebug): void {
  const panel = document.getElementById("sun-clock");
  if (!panel) return;
  const dateEl = document.getElementById("sun-date");
  const up = document.getElementById("sun-year-up");
  const dn = document.getElementById("sun-year-dn");
  const light = document.getElementById("sun-light");
  // Wave 2: name the icon-only sun-clock buttons for hover + a11y.
  const setTip = (el: HTMLElement | null, t: string): void => { if (el) { el.title = t; el.setAttribute("aria-label", t); } };
  setTip(up, "Step the displayed calendar year forward (the sun angle follows the simulated season + hour).");
  setTip(dn, "Step the displayed calendar year back.");
  setTip(light, "Toggle globe sun-lighting — shadows track the simulated hour and season.");
  setTip(dateEl, "The calendar instant the globe's sun is using — it follows the simulator's season + hour.");
  let lightingOn = true;
  const refresh = (): void => {
    const d = viz._debug.simDate;
    if (dateEl) dateEl.textContent = d ? d.label : "—";
  };
  up?.addEventListener("click", () => { viz._debug.setDisplayYear(viz._debug.displayYear + 1); refresh(); });
  dn?.addEventListener("click", () => { viz._debug.setDisplayYear(viz._debug.displayYear - 1); refresh(); });
  light?.addEventListener("click", () => {
    lightingOn = !lightingOn;
    viz._debug.setLighting(lightingOn);
    light.classList.toggle("active", lightingOn);
  });
  sim.subscribe(refresh); // re-label as the sim's hour/season advance
  refresh();
}

// P-MOBILE-V3: the phone-only LEFT side drawer's pull-tab handle — toggles `html.gta-drawer-collapsed`
// (index.html's CSS slides BOTH the drawer, via translateX, AND this handle, via its `left`, off the same
// class in lockstep — a real transform+transition, not a teleport). Purely a UI convenience; no workspace
// state (open/closed panels) is affected by collapsing the drawer, only its on-screen visibility, so the
// user can hide the whole panel column for an unobstructed map and slide it back. The handle shows a
// chevron pointing the way it will move: "‹" when the drawer is open (tap to slide it away left), "›" when
// collapsed (tap to pull it back).
function wireMobileDrawerHandle(): void {
  const handle = document.getElementById("mobile-drawer-handle");
  if (!handle) return;
  const setLabel = (collapsed: boolean): void => {
    handle.textContent = collapsed ? "›" : "‹";
    handle.setAttribute("aria-expanded", String(!collapsed));
  };
  setLabel(false); // drawer starts open
  handle.addEventListener("click", () => {
    setLabel(document.documentElement.classList.toggle("gta-drawer-collapsed"));
  });
}

// Main-thread copy of the reduced model — needed for the render geometry (VizModel) and the static
// node/edge ordering the snapshot packer encodes against (PackContext). buildSimModel is deterministic,
// so this reproduces the worker's ordering exactly (the worker fetches + builds its own copy for the
// engine). Mirrors sim.worker.ts#loadModel; both are same-origin reads of bundled assets (R1).
async function fetchModel(baseUrl: string): Promise<SimModel> {
  const base = baseUrl.replace(/\/$/, "");
  const json = async (p: string): Promise<unknown> => {
    const res = await fetch(`${base}/${p}`);
    if (!res.ok) throw new Error(`app: failed to load ${p} (${res.status})`);
    return res.json();
  };
  const [nodes, edges, generators, loads, report, catalog] = await Promise.all([
    json("reduced/nodes.json"), json("reduced/edges.json"), json("reduced/generators.json"),
    json("reduced/loads.json"), json("reduced/reduction_report.json"), json("scenarios.json"),
  ]);
  const scenarios = (catalog as { anchors: Scenario[] }).anchors;
  return buildSimModel({ nodes, edges, generators, loads, report }, scenarios);
}

// v3 display-fidelity extras (facility parcels / connection points / pruned wiring). Best-effort:
// a missing file (older data build) degrades to the solver-only view, never blocks boot.
async function fetchDisplayExtras(baseUrl: string): Promise<DisplayExtras> {
  const base = baseUrl.replace(/\/$/, "");
  const opt = async <T>(p: string): Promise<T | undefined> => {
    try {
      const res = await fetch(`${base}/${p}`);
      return res.ok ? ((await res.json()) as T) : undefined;
    } catch {
      return undefined;
    }
  };
  const [facilityParcels, connectionPoints, prunedLines] = await Promise.all([
    opt<DisplayExtras["facilityParcels"]>("reduced/facility_parcels.json"),
    opt<DisplayExtras["connectionPoints"]>("reduced/connection_points.json"),
    opt<DisplayExtras["prunedLines"]>("reduced/pruned_lines.json"),
  ]);
  return {
    ...(facilityParcels ? { facilityParcels } : {}),
    ...(connectionPoints ? { connectionPoints } : {}),
    ...(prunedLines ? { prunedLines } : {}),
  };
}

async function boot(): Promise<void> {
  autoReloadOnSwUpdate(); // listen BEFORE the long model fetch — updates can land mid-boot

  // P-MOBILE: device detection FIRST — before the (network-bound) model fetch — so the mobile
  // bottom-drawer layout is live from the very first frame, not just after the round-trip.
  // `env` also feeds detectStartTier below (computed once, reused).
  // P-MOBILE-FUNDAMENTAL (2026-07-06 pt.4): this used to also gate a hard, full-screen "please rotate to
  // landscape" modal — retired. Its own detection (screen.orientation.type) could itself misreport (a
  // genuinely-portrait 390×844 viewport observed reporting "landscape-primary" under automation, silently
  // defeating the gate), and separately, the mobile layout's real bug was panels overlapping EACH OTHER —
  // fixed for good in P-MOBILE-V2 (2026-07-07 pt.7, see index.html) by making every panel a normal in-flow
  // block stacked inside ONE bottom-anchored, height-capped drawer, so overlap is geometrically impossible
  // regardless of orientation, not something a positioning rule has to get right. The rotate requirement
  // was solving a problem the layout no longer has, at the cost of a fragile blocking surface. Portrait
  // and landscape are both fully supported now.
  const env = readBrowserEnv();
  const detectedMobile = isMobilePhone(env);
  // Manual override for QA/support diagnosability (adversarial review, 2026-07-06: a wrong boot-time
  // classification otherwise has no in-session recovery) — ?mobileUi=1 forces the phone layout on,
  // ?mobileUi=0 forces it off, regardless of what the device signals say.
  const mobileOverride = new URLSearchParams(location.search).get("mobileUi");
  const mobile = mobileOverride === "1" ? true : mobileOverride === "0" ? false : detectedMobile;
  console.info("gta: mobile-UI detection", { coarsePointer: env.coarsePointer, touch: env.touch, smallScreen: env.smallScreen, uaMobile: env.uaMobile, detectedMobile, mobileOverride, mobile });
  if (mobile) document.documentElement.classList.add("gta-mobile-tabs");

  const [model, display] = await Promise.all([fetchModel(MODEL_URL), fetchDisplayExtras(MODEL_URL)]);
  const vizModel: VizModel = { nodes: model.nodes, edges: model.edges, loads: model.loads };
  const context: PackContext = {
    nodeIds: model.nodes.map((n) => n.id),
    edgeIds: model.edges.map((e) => e.id),
  };

  // real type:'module' worker, adapted to the transport-agnostic WorkerLike the client speaks.
  const rawWorker = new Worker(new URL("../worker/sim.worker.ts", import.meta.url), { type: "module" });
  const workerLike: WorkerLike = {
    postMessage: (msg: MainToWorker, transfer?: Transferable[]) => rawWorker.postMessage(msg, transfer ?? []),
    onmessage: null,
  };
  rawWorker.onmessage = (ev: MessageEvent): void => workerLike.onmessage?.({ data: ev.data as WorkerToMain });
  const worker = createSimWorkerClient({ worker: workerLike, context });

  // viz adapter, wrapped to time snapshot-apply so the governor can read main-thread apply-lag.
  const baseViz = createCesiumAdapter({ generators: model.generators, display });
  let lastApplyMs = 0;
  const viz: CesiumAdapter = {
    mount: (c, m) => { baseViz.mount(c, m); baseViz.setBasemap(INITIAL_BASEMAP); },
    applySnapshot: (s) => {
      const t0 = performance.now();
      baseViz.applySnapshot(s);
      lastApplyMs = performance.now() - t0;
    },
    setLayerVisible: (l, v) => baseViz.setLayerVisible(l, v),
    setQuality: (t) => baseViz.setQuality(t),
    setBasemap: (k, o) => baseViz.setBasemap(k, o),
    onPick: (cb) => baseViz.onPick(cb),
    dispose: () => baseViz.dispose(),
  };

  // adaptive-perf governor v2: 3-state machine (STABLE / DEGRADED / PROBING) that PROACTIVELY probes
  // one tier up every few seconds when the device holds ≥40fps, rolling back on the 28fps hard floor.
  // Starts at the multi-layered env recommendation (software-WebGL → Save-Data → form-factor → battery
  // → CPU/memory) and floats from there on live frame timing. `env` was already read above (P-MOBILE).
  const startTier = detectStartTier(env);
  const governor = makeGovernor({
    setQuality: (tier) => viz.setQuality(tier),
    setSpeed: (speed) => sim.getState().setSpeed(speed),
    getApplyLagMs: () => lastApplyMs,
    startQuality: startTier,
  });

  // P-DESKTOP-FLOAT (2026-07-06 pt.5): captured via the onWorkspaceReady side-channel below — a narrow
  // drag-handle onto the live workspace, so app/draggable-panels.ts's real pointer-drag mechanics can
  // move/raise floating panels without compose.ts's mountDashboards contract needing to change at all.
  let workspaceHandle: DragHandle | null = null;

  await composeApp({
    worker,
    store: sim,
    setTransport,
    viz,
    // dashboards with the static model threaded in → the full object inspector (specs + live graph)
    mountDashboards: mountDashboardsWithModel(
      { nodes: model.nodes, edges: model.edges, loads: model.loads, generators: model.generators },
      (on) => baseViz._debug.setVoltageOverlay(on), // Phase 1.1: wire the voltage overlay toggle to the viz _debug seam
      mobile, // P-MOBILE: only "controls" boots open on a phone — everything else starts as a corner tab
      (ws) => { workspaceHandle = ws; },
    ),
    model: vizModel,
    cesiumRoot: requireEl("cesium"),
    dashRoot: requireEl("workspace"),
    modelUrl: MODEL_URL,
    governor,
    autostart: true,
  });

  // P-DESKTOP-FLOAT: real pointer-drag wiring for every panel workspace.ts marked `float:true` — the
  // workspace (and every panel's DOM) is fully mounted by the time composeApp() above resolves (its
  // internal mountDashboards call is synchronous), so querying for `.wpanel.floating .wpanel-head`
  // headers here finds everything, open or closed. No-ops harmlessly if workspaceHandle somehow never
  // fired (e.g. a future dashboards refactor drops the callback) rather than throwing.
  // EXPLICITLY desktop/laptop-only (`!mobile`), per the user's own scoping of this feature: the SAME
  // panels carry `float:true` (and hence the `.floating` class) on a phone too — mobile's own
  // higher-specificity CSS already wins the *position*, but a real pointerdown/setPointerCapture
  // handler on every `.wpanel.floating .wpanel-head` would still fire on a phone's tab headers and could
  // intercept the just-shipped tap-to-open gesture (a touch that moves even slightly mid-tap would read
  // as a drag). Gating here is simpler and safer than trying to make the drag math touch-aware.
  if (!mobile) mountDraggablePanels(document, workspaceHandle);
  if (mobile) wireMobileDrawerHandle(); // P-MOBILE-V2: the bottom-drawer slide handle, phone-only

  wireBasemapSwitcher(viz);
  wireSunClock(baseViz); // date label + year stepper + sun-lighting toggle (reads the VizDebug seam)

  // Apply the detected tier NOW so resolution + effects are correct from the FIRST painted frame — the
  // governor only changes tiers on sustained streaks, it never sets the initial one. This is also where
  // the HiDPI/4K blur fix lands (setQuality flips off useBrowserRecommendedResolution).
  viz.setQuality(startTier);
  if (startTier === "low") {
    console.info("gta: starting at LOW quality (software WebGL / small device / low battery / Save-Data) — the governor restores quality if the device proves faster");
  }

  // Ambient battery watcher (Chromium-only; safe no-op on Safari/Firefox). On a charging-state change
  // (e.g. plugged into mains after a low-battery start), nudge the governor to re-probe upward right
  // away instead of waiting for the cadence timer. The watch is held for the app lifetime. The live
  // battery state is kept in `liveBattery` so the HUD can show it; `env` itself is a one-shot snapshot.
  const navForBattery = navigator as Navigator & { getBattery?: () => Promise<unknown> };
  let liveBattery = env.battery;
  let lastCharging = liveBattery?.charging ?? null;
  const batteryWatch = watchBattery(navForBattery.getBattery, (b) => {
    liveBattery = b;
    if (lastCharging !== null && b.charging && !lastCharging) {
      // just plugged in → headroom likely recovered; ask the governor to climb now
      governor.requestReprobe();
    }
    lastCharging = b.charging;
  });

  // Perf HUD (index.html #perf-hud): the corner readout of render resolution · FPS+tier+mode · achieved
  // sim Hz · ambient (battery/network) — the feedback signals the adaptive system trades. Reads non-
  // contract introspection only.
  mountPerfHud({
    root: document.getElementById("perf-hud"),
    getFps: () => governor.fps,
    getQuality: () => governor.quality,
    getMode: () => governor.mode,
    getResolution: () => baseViz._debug.renderResolution(),
    getTick: () => baseViz._debug.lastSnapshot?.tick ?? null,
    getEnv: () => env,
    getBattery: () => liveBattery,
  });

  // P-LAUNCHER (2026-07-06 pt.3, adversarial re-audit): the launcher chip bar now WRAPS onto multiple
  // rows (index.html .wks-launcher) instead of hiding most chips behind an invisible horizontal scroll.
  // A wrapped bar's real height varies with viewport width, so measure it live and republish as a CSS
  // custom property the side-drawers read for their top clearance — never a hand-tuned guess that could
  // still collide with a taller-than-expected launcher.
  // Guarded like every other browser-API read in this file (adversarial review, 2026-07-06 pt.4):
  // ResizeObserver is unsupported on a small set of pre-2020 browsers; without this guard `boot()` would
  // throw uncaught (bare `void boot()`, no catch) and silently drop everything after these two calls.
  const observeResize: ((target: HTMLElement, cb: () => void) => { disconnect(): void }) | undefined =
    typeof ResizeObserver === "function"
      ? (target, cb) => {
          const ro = new ResizeObserver(cb);
          ro.observe(target);
          return ro;
        }
      : undefined;
  mountElementClearance({
    element: document.querySelector<HTMLElement>(".wks-launcher"),
    cssVar: "--launcher-clearance",
    setProperty: (n, v) => document.documentElement.style.setProperty(n, v),
    ...(observeResize ? { observe: observeResize } : {}),
  });
  // Wave 6 (M4): the second `mountElementClearance` publishing `--mobile-totals-clearance` was REMOVED — dead
  // wiring. Since the P-DESKTOP-FLOAT/mobile-tabs rework the mobile panels are normal in-flow blocks stacked
  // inside the one scrolling drawer, so the pinned totals headline no longer needs a measured clearance var, and
  // a repo-wide grep confirmed NOTHING consumed `var(--mobile-totals-clearance)`. The --launcher-clearance
  // publisher above is the live one (the side-drawer top padding reads it).

  // Support/verification handle (non-contract): the adapter's VizDebug seam + the store + the battery
  // watch (held here for the app lifetime so its listener is not GC'd) — reachable from the console.
  // Read-only introspection + the deterministic _debug.lookAt camera jump — used by live diagnostics;
  // never imported by any module (runtime glue only, like the rest of boot()).
  (globalThis as unknown as Record<string, unknown>)["__gta"] = { viz: baseViz, store: sim, batteryWatch, governor };
}

void boot();
